import { CheckRounded } from "@mui/icons-material";
import { Button, MenuItem, TextField } from "@mui/material";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import { useChecklistRouteHook } from "../../hooks/useChecklistRouteHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";
import Flx from "../layout/Flx";
import BasicModal from "../modals/BasicModal";
import Txt from "../typography/Txt";

// ── Palette (slate-leaning neutrals) ──────────────────────────────────────────
const palette = {
  border: "#e2e8f0",
  surface: "#ffffff",
  text: "#0f172a",
  textMuted: "#475569",
  textLabel: "#64748b",
};

// ── Supported update types ───────────────────────────────────────────────────
// "exception"       → /checklists/update_exception_request/{dealId}/{exceptionId}
// "borrower"        → /underwriting/update_borrower/{loanId}/{borrowerId}
// "subjectProperty" → /underwriting/update_subject_property/{loanId}/{propertyId}
// "loan"            → /underwriting/update_loan_data/{loanId}
export const UPDATE_TYPES = {
  EXCEPTION: "exception",
  BORROWER: "borrower",
  SUBJECT_PROPERTY: "subjectProperty",
  LOAN: "loan",
};

const DEFAULT_TITLES = {
  exception: "Update Exception",
  borrower: "Update Borrower",
  subjectProperty: "Update Property",
  loan: "Update Loan",
};

const DEFAULT_SUCCESS_MESSAGE = {
  exception: "Exception updated",
  borrower: "Borrower updated",
  subjectProperty: "Property updated",
  loan: "Loan updated",
};

/**
 * Reusable single-purpose field update modal.
 *
 * Props
 *   open            — modal visibility
 *   onClose         — close handler
 *   updateType      — one of UPDATE_TYPES
 *   context         — type-specific IDs:
 *                       exception        → { dealId, exceptionId }
 *                       borrower         → { dealId, borrowerId }   // dealId alias of loanId
 *                       subjectProperty  → { dealId, propertyId }
 *                       loan             → { dealId }
 *   fields          — array of field descriptors (one or many):
 *                       { name, label, type: "text"|"multiline"|"select",
 *                         placeholder?, helperText?, rows?, options?, required? }
 *   initialValues   — { [fieldName]: value } — pre-fills the form
 *   title           — optional custom modal title
 *   description     — optional helper text under the title
 *   submitLabel     — optional override for the submit button label
 *   onSuccess       — callback fired with the API response
 *   dispatchLoanDrilldownSet — defaults true; set false if the response is not the loan
 */
const FieldUpdateModal = ({
  open,
  onClose,
  updateType = UPDATE_TYPES.EXCEPTION,
  context = {},
  fields = [],
  initialValues = {},
  title,
  description,
  submitLabel = "Save",
  onSuccess,
  dispatchLoanDrilldownSet = true,
}) => {
  const dispatch = useDispatch();
  const { updateBorrower, updateSubjectProperty, updateLoanData } =
    useUnderwritingHook();
  const { updateChecklistException, loading: checklistLoading } =
    useChecklistRouteHook();

  const [values, setValues] = useState(() => ({ ...initialValues }));
  const [submitting, setSubmitting] = useState(false);

  // Reset the form whenever the modal is opened or initialValues change
  useEffect(() => {
    if (open) setValues({ ...initialValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(initialValues)]);

  const resolvedTitle =
    title || DEFAULT_TITLES[updateType] || "Update";

  const setField = useCallback((name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
  }, []);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    // Required fields must be non-empty
    for (const f of fields) {
      if (!f.required) continue;
      const v = values[f.name];
      if (isNil(v) || (typeof v === "string" && v.trim() === "")) return false;
    }
    // At least one field must have changed
    const dirty = fields.some((f) => values[f.name] !== initialValues[f.name]);
    return dirty;
  }, [fields, values, initialValues, submitting]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  const handleSuccess = useCallback(
    (response) => {
      if (dispatchLoanDrilldownSet && response && !isNil(response?._id)) {
        dispatch(loanDrilldownSet(response));
      }
      if (isFunction(onSuccess)) onSuccess(response);
      setSubmitting(false);
      onClose?.();
    },
    [dispatch, dispatchLoanDrilldownSet, onClose, onSuccess],
  );

  const handleFail = useCallback(() => setSubmitting(false), []);

  const buildPayload = useCallback(() => {
    // Only send fields that changed — server merges, so partial payloads are fine.
    const payload = {};
    fields.forEach((f) => {
      if (values[f.name] !== initialValues[f.name]) {
        payload[f.name] = values[f.name];
      }
    });
    return payload;
  }, [fields, values, initialValues]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    const payload = buildPayload();
    setSubmitting(true);

    switch (updateType) {
      case UPDATE_TYPES.EXCEPTION:
        updateChecklistException({
          dealId: context.dealId,
          exceptionId: context.exceptionId,
          data: payload,
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
          onSuccessMsg: DEFAULT_SUCCESS_MESSAGE.exception,
        });
        break;
      case UPDATE_TYPES.BORROWER:
        updateBorrower({
          loanId: context.dealId || context.loanId,
          borrowerId: context.borrowerId,
          data: payload,
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
        });
        break;
      case UPDATE_TYPES.SUBJECT_PROPERTY:
        updateSubjectProperty({
          loanId: context.dealId || context.loanId,
          propertyId: context.propertyId,
          data: payload,
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
        });
        break;
      case UPDATE_TYPES.LOAN:
        updateLoanData({
          loanId: context.dealId || context.loanId,
          data: payload,
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
        });
        break;
      default:
        console.warn(`FieldUpdateModal: unsupported updateType "${updateType}"`);
        setSubmitting(false);
    }
  }, [
    canSubmit,
    buildPayload,
    updateType,
    context,
    updateChecklistException,
    updateBorrower,
    updateSubjectProperty,
    updateLoanData,
    handleSuccess,
    handleFail,
  ]);

  const loading = submitting || checklistLoading;

  return (
    <BasicModal
      show={open}
      onClose={handleClose}
      title={resolvedTitle}
      maxWidth="sm"
    >
      <Flx column g={2}>
        {description && (
          <Txt sx={{ fontSize: 12.5, color: palette.textMuted, lineHeight: 1.5 }}>
            {description}
          </Txt>
        )}

        <Flx column g={2}>
          {fields.map((f) => (
            <FieldRenderer
              key={f.name}
              field={f}
              value={values[f.name] ?? ""}
              onChange={(v) => setField(f.name, v)}
              disabled={loading}
            />
          ))}
        </Flx>

        <Flx ac jc="flex-end" g={1} sx={{ pt: 0.5 }}>
          <Button
            variant="text"
            onClick={handleClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              color: palette.textMuted,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={<CheckRounded sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {loading ? "Saving…" : submitLabel}
          </Button>
        </Flx>
      </Flx>
    </BasicModal>
  );
};

/* ─── Field renderer ─────────────────────────────────────────────────────── */

const FieldRenderer = ({ field, value, onChange, disabled }) => {
  const {
    name,
    label,
    type = "text",
    placeholder,
    helperText,
    rows = 4,
    options = [],
    required,
  } = field;

  const commonProps = {
    name,
    label,
    placeholder,
    helperText,
    required,
    fullWidth: true,
    size: "small",
    disabled,
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    sx: {
      "& .MuiInputBase-input": { fontSize: 13.5 },
      "& .MuiInputLabel-root": { fontSize: 13 },
      "& .MuiFormHelperText-root": { fontSize: 11, color: palette.textLabel },
    },
  };

  if (type === "select") {
    return (
      <TextField {...commonProps} select>
        {options.map((opt) => {
          const optValue = typeof opt === "object" ? opt.value : opt;
          const optLabel = typeof opt === "object" ? opt.label : opt;
          return (
            <MenuItem key={optValue} value={optValue}>
              {optLabel}
            </MenuItem>
          );
        })}
      </TextField>
    );
  }

  if (type === "multiline") {
    return (
      <TextField
        {...commonProps}
        multiline
        minRows={rows}
        maxRows={Math.max(rows + 4, 10)}
      />
    );
  }

  // default: single-line text
  return <TextField {...commonProps} />;
};

export default FieldUpdateModal;
